import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { v4 } from "uuid";
import { RCCHookToast, RCCRefModalProps, RCCServicesApi, RCCButton } from "react-common-components";
import { useFieldArray, useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import Tabs from "src/components/Tabs";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useAuth } from "src/context/Auth/context";
import stringToNumber from "src/utils/stringToNumber";
import { baseIqtbc002, ClosedPeriods, EvaluationForm, IIndicatorForm, IIqtbc002, IIqtbc004, IIqtbc005, IRefIndicatorModal, GraphicComp, GraphicColors, GraphicOrientation, ValueIndicator, ITablesRelated } from "./interface";
import TabGeneralData from "./modalTabs/tabGeneralData";
import TabAdvanced from "./modalTabs/tabAdvanced";
import TabMetrics from "./modalTabs/tabMetrics";
import TabGoals from "./modalTabs/tabGoals";
import TabGraphic from "./modalTabs/tabGraphics";
import TabFields from "./modalTabs/tabFields";
import TabSimpleForm from "./modalTabs/tabSimpleForm";
import TabPermissions from "./modalTabs/tabPermissions";
import { completePermissionType } from "../Permissions/interface";
import indicatorFormSchema from "./schemas";

const IndicatorModal: React.ForwardRefRenderFunction<IRefIndicatorModal> = (_, ref) => {
	const { translate,currentLanguage} = useTranslation();
	const { addToast} = RCCHookToast();
	const { user } = useAuth();
	const { setLoading } = useLoaderBackdrop();
	
	// Modal Add/Change
	const [isAdd, setIsAdd] = useState(false);
	const [modalTitle,setModalTitle] = useState<string>("");
	const [subtitle, setSubtitle] = useState<string | undefined>(undefined);
	const [simpleForm,setSimpleForm] = useState(false);
	const [isDuplicate, setIsDuplicate] = useState(false);
	const [indicator, setIndicator] = useState<IIndicatorForm>({indicator:{}} as IIndicatorForm);
	const [onCloseFunction,setOnCloseFunction] = useState<((indicator:IIndicatorForm)=>void )>();
	const modalIndicator = useRef<RCCRefModalProps>(null);

	const { 
		register,
		control, 
		unregister,
		formState, 
		setError,
		clearErrors,
		setValue,
		reset,
		handleSubmit
	}= useForm<IIndicatorForm>(
		{
			defaultValues:{
				indicator:{
					iqc001Qtdeperant:2,
					iqc001Formaaval:EvaluationForm.TO_UP,
					iqc001Tendencia:false,
					iqc001Ocultarformaaval:false,
					iqc001Calcularacum:false,
					iqc001Verificafca:false,
					iqc001Periodofechado:ClosedPeriods.CLOSED_ONLY,
					iqc001Compgrafico:GraphicComp.SIDE_BY_SIDE,
					iqc001Corgrafico:GraphicColors.METRICS,
					iqc001Labelvalores:GraphicOrientation.HORIZONTAL,
					iqc001Mostrarvalor:ValueIndicator.PERCENTUAL
				},
				goal:{...baseIqtbc002},
				compromise:{...baseIqtbc002},
				competitiveness:{...baseIqtbc002}
			} as IIndicatorForm
		}
	);

	// Array controls
	const metricControls = useFieldArray({
		control,
		name: "metrics"
	});

	const fieldsControls = useFieldArray({
		control,
		name: "fields"
	});

	const submitIndicator = async(data: any) => {
		try{
			clearErrors();
			modalIndicator.current?.setLoadingButton();
			const dataForm = data as IIndicatorForm;
			dataForm.iqc003Id = indicator.iqc003Id;	
			dataForm.indicator.iqc001Id = indicator.indicator.iqc001Id;
			dataForm.indicator.iqc001Qtdeperant = dataForm.indicator.iqc001Calcularacum ? dataForm.indicator.iqc001Qtdeperant :0;

			dataForm.indicator.iqc001Escalamax = typeof dataForm.indicator.iqc001Escalamax === "string" ? stringToNumber(dataForm.indicator.iqc001Escalamax, currentLanguage) || 0 : dataForm.indicator.iqc001Escalamax;
			dataForm.indicator.iqc001Escalamaxdir = typeof dataForm.indicator.iqc001Escalamaxdir === "string" ? stringToNumber(dataForm.indicator.iqc001Escalamaxdir, currentLanguage) || 0 : dataForm.indicator.iqc001Escalamaxdir;
			dataForm.indicator.iqc001Escalamin = typeof dataForm.indicator.iqc001Escalamin === "string" ? stringToNumber(dataForm.indicator.iqc001Escalamin, currentLanguage) || 0 : dataForm.indicator.iqc001Escalamin;
			dataForm.indicator.iqc001Escalamindir = typeof dataForm.indicator.iqc001Escalamindir === "string" ? stringToNumber(dataForm.indicator.iqc001Escalamindir, currentLanguage) || 0 : dataForm.indicator.iqc001Escalamindir;

			if(simpleForm){
				dataForm.indicator.iqc001DecimaisPainel = 2;
				dataForm.indicator.iqc001PaletaCores = 1;
			}

			if(typeof data.tags === "string"){
				if(data.tags){
					dataForm.tags = data.tags.split(",");
				}
				else{
					dataForm.tags = [];
				}
			}else if(dataForm.tags && dataForm.tags.length > 0)
				dataForm.tags = dataForm.tags.map((tag: any) => {
					if(typeof tag === "object")
						return tag?.value;
					return tag;
				});

			if(dataForm.permissions && dataForm.permissions.length > 0){
				dataForm.permissions = dataForm.permissions.map((permiss: any) => {
					if(typeof permiss.iqc005Efa005Id === "object" && !!permiss.iqc005Efa005Id)
						return {
							...permiss,
							iqc005Permissao: Number(permiss.iqc005Permissao),
							iqc005Efa005Id: permiss.iqc005Efa005Id?.value
						};
					if(typeof permiss.iqc005Efa004Id === "object" && !!permiss.iqc005Efa004Id)
						return {
							...permiss,
							iqc005Permissao: Number(permiss.iqc005Permissao),
							iqc005Efa004Id: permiss.iqc005Efa004Id?.value
						};
					return permiss;
				}).filter((e) => e != null);

				// verifica se tem permissão de Visializar e Editar
				if(!dataForm.permissions.filter((x) => x.iqc005Permissao.toString() === completePermissionType.VIEW_AND_EDIT).length){
					modalIndicator.current?.resetLoadingButton();
					addToast({
						color: "yellow",
						description: translate("Ao menos uma permissão deve ser (Visualizar e Editar)"),
						title: translate("Atenção")
					});
					return;
				}
			}

			if(dataForm.fields && dataForm.fields.length > 0){
				dataForm.fields = dataForm.fields.filter((x) => x.id).map((field: any,index:number) => {
					let divider = field.iqc002Divisor; 
					let dividend = field.iqc002Dividendo; 
					let fieldId = field.iqc002Iqb001Id;
					if(typeof field.iqc002Iqb001Id === "object" && !!field.iqc002Iqb001Id)
						fieldId= field.iqc002Iqb001Id?.value;
					if(typeof field.iqc002Dividendo === "object" && !!field.iqc002Dividendo)
						dividend = field.iqc002Dividendo?.value; 
					if(typeof field.iqc002Divisor === "object" && !!field.iqc002Dividendo)
						divider = field.iqc002Divisor?.value;

					return {
						...field,
						iqc002Ordem:index+1,
						iqc002Iqb001Id:fieldId === "0" ? null : fieldId,
						iqc002Dividendo:dividend,
						iqc002Divisor:divider
					};
				});

				// verifica se tem permissão de Visializar e Editar
				if(!dataForm.fields.filter((x) => x.id === dataForm.selectedGoalField).length){
					modalIndicator.current?.resetLoadingButton();
					addToast({
						color: "yellow",
						description: translate("Ao menos um campo deve estar marcado para comparar com a meta"),
						title: translate("Atenção")
					});
					return;
				}
			}
			if(typeof data.goal!.iqc002Dividendo === "object" 
				&& !!data.goal!.iqc002Dividendo)
				dataForm.goal!.iqc002Dividendo = data.goal!.iqc002Dividendo?.value; 
			if(typeof data.goal!.iqc002Divisor === "object" 
				&& !!data.goal!.iqc002Divisor)
				dataForm.goal!.iqc002Divisor = data.goal!.iqc002Divisor?.value;

			dataForm.goal.iqc002Metadefault = stringToNumber(data.goal.iqc002Metadefault, currentLanguage);

			if(data.useCompetitiveness){
				dataForm.competitiveness!.decimalPlaces = dataForm.goal.decimalPlaces;
				dataForm.competitiveness!.iqc002Tipografico = dataForm.goal.iqc002Tipografico;
				dataForm.competitiveness!.iqc002Tpgrafacum = dataForm.goal.iqc002Tpgrafacum;
				dataForm.competitiveness!.iqc002Tipoacum = dataForm.goal.iqc002Tipoacum;
				dataForm.competitiveness!.iqc002Mostrartabela = dataForm.goal.iqc002Mostrartabela;
				dataForm.competitiveness!.iqc002Metadefault = stringToNumber(data.competitiveness.iqc002Metadefault, currentLanguage);
				if(typeof data.competitiveness!.iqc002Dividendo === "object" 
					&& !!data.competitiveness!.iqc002Dividendo)
					dataForm.competitiveness!.iqc002Dividendo = data.competitiveness!.iqc002Dividendo?.value; 
				if(typeof data.competitiveness!.iqc002Divisor === "object" 
					&& !!data.competitiveness!.iqc002Divisor)
					dataForm.competitiveness!.iqc002Divisor = data.competitiveness!.iqc002Divisor?.value;
			}else{
				dataForm.competitiveness = undefined;
			}
				
			if(data.useCompromise){
				dataForm.compromise!.decimalPlaces = dataForm.goal.decimalPlaces;
				dataForm.compromise!.iqc002Tipografico = dataForm.goal.iqc002Tipografico;
				dataForm.compromise!.iqc002Tpgrafacum = dataForm.goal.iqc002Tpgrafacum;
				dataForm.compromise!.iqc002Tipoacum = dataForm.goal.iqc002Tipoacum;
				dataForm.compromise!.iqc002Mostrartabela = dataForm.goal.iqc002Mostrartabela;
				dataForm.compromise!.iqc002Metadefault = stringToNumber(data.compromise.iqc002Metadefault, currentLanguage);
				if(typeof data.compromise!.iqc002Dividendo === "object" 
					&& !!data.compromise!.iqc002Dividendo)
					dataForm.compromise!.iqc002Dividendo = data.compromise!.iqc002Dividendo?.value; 
				if(typeof data.compromise!.iqc002Divisor === "object" 
					&& !!data.compromise!.iqc002Divisor)
					dataForm.compromise!.iqc002Divisor = data.compromise!.iqc002Divisor?.value;
			}else{
				dataForm.compromise = undefined;
			}
				
			if(data.metrics && data.metrics.length > 0)
				dataForm.metrics = data.metrics.map((metric: any) => ({
					...metric,
					iqc004Valorfim: stringToNumber(metric.iqc004Valorfim, currentLanguage),
					iqc004Valorini: stringToNumber(metric.iqc004Valorini, currentLanguage)
				}));

			await indicatorFormSchema(translate,currentLanguage).validate({...dataForm}, {
				abortEarly: false,
				context:{
					isAdd:isAdd||isDuplicate,
					useCompetitiveness:data.useCompetitiveness,
					useCompromise:data.useCompromise
				}
			});
			
			dataForm.indicator.iqc001Taa006Id = Number(data.indicator.iqc001Taa006Id) === 0 ? null : data.indicator.iqc001Taa006Id;

			let response;
			if(isAdd || isDuplicate){
				response = await RCCServicesApi.postRequest({
					method: "indicators/Insert",
					body: {
						...dataForm,
						subsidiaryId: indicator.subsidiaryId ?? null,
						statementLineId: indicator.statementLineId ?? null	
					}
				});

			}else{
				response = await RCCServicesApi.putRequest({
					method: "indicators/Update",
					body: {
						...dataForm
					}
				});
			}

			addToast({
				color: "green",
				description: isAdd||isDuplicate ? translate("Indicador adicionado com sucesso") : translate("Indicador alterado com sucesso"),
				title: translate("Sucesso")
			});

			if(onCloseFunction)
				onCloseFunction(response.data as IIndicatorForm);

			modalIndicator.current?.resetLoadingButton();
			modalIndicator.current?.closeModal();

		}catch(err){
			modalIndicator.current?.resetLoadingButton();
			if(getValidationErrors(err, 
				setError)){
				addToast({
					color: "yellow",
					description: translate("Verifique os campos e tente novamente"),
					title: translate("Atenção")
				});
				return;
			}
				
			standardErrorMessages(err, isAdd||isDuplicate ? translate("Erro ao adicionar indicador") : translate("Erro ao alterar indicador"), addToast);
		}
	};

	const actionButtonAdd = (onClose?:(indicator?:IIndicatorForm)=>void, tablesRelated?: ITablesRelated) => {
		reset();
		setSubtitle(undefined);
		const initialPermission =  [{
			id:v4(),
			iqc005Id:0,
			iqc005Efa005Id:user.efa005Id,
			efa005Nome:user.efa005Nome,
			iqc005Permissao:completePermissionType.VIEW_AND_EDIT
		}] as IIqtbc005[];
		setIndicator({
			indicator:{
				iqc001Qtdeperant:2,
				iqc001Formaaval:EvaluationForm.TO_UP,
				iqc001Periodofechado:ClosedPeriods.CLOSED_ONLY,
				iqc001Efa005Id: user.efa005Id,
				iqc001DecimaisPainel: 2
			},
			metrics:[] as IIqtbc004[],
			fields:[] as IIqtbc002[],
			goal:{...baseIqtbc002},
			permissions:initialPermission,
			statementLineId: tablesRelated?.statementLineId ?? null,
			subsidiaryId: tablesRelated?.subsidiaryId ?? null
		} as IIndicatorForm);
		setIsAdd(true); 
		setSimpleForm(true);
		setOnCloseFunction(() => onClose);

		metricControls.append([
			{iqc004Id:0,iqc004Cor:"#D91E18", iqc004Valorini:0, iqc004Valorfim:80.99, id:v4()},
			{iqc004Id:0,iqc004Cor:"#F5AB35", iqc004Valorini:81, iqc004Valorfim:99.99, id:v4()},
			{iqc004Id:0,iqc004Cor:"#019875", iqc004Valorini:100, iqc004Valorfim:9999, id:v4()}
		]);
		setValue("permissions",initialPermission);
		setModalTitle(translate("Adicionar indicador"));
		modalIndicator.current?.openModal();
	};

	const actionButtonChange = async (id:number,duplicate:boolean,onClose?:(indicator:IIndicatorForm)=>void) => {
		try {
			setLoading(true);
			reset();
			setIsAdd(false);
			setSimpleForm(false);
			setIsDuplicate(duplicate);
			setOnCloseFunction(() => onClose);

			if(duplicate){
				setModalTitle(translate("Copiar indicador"));
			}else{
				setModalTitle(translate("Alterar indicador"));
			}

			await requestIndicator(id,duplicate);
			setLoading(false);
			modalIndicator.current?.openModal();
		} catch (error) {
			standardErrorMessages(error,translate("Erro ao encontrar indicador"), addToast);
			setLoading(false);
		}
	};

	const requestIndicator = async(id:number,duplicate:boolean) => {
		
		const {data} = await RCCServicesApi.getRequest({
			method: "indicators/Select",
			urlParameters: {
				id
			}
		});

		const indicador = data;

		setIndicator(() => {
			if(duplicate){
				indicador.indicator.iqc001Codigo = "";

				indicador.goal = {
					...indicador.goal,
					iqc002Id: 0
				};

				if(indicador.compromise){
					indicador.compromise = {
						...indicador.compromise,
						iqc002Id: 0
					};
				}

				if(indicador.competitiveness){
					indicador.competitiveness = {
						...indicador.competitiveness,
						iqc002Id: 0
					};
				}
				return indicador;
			}
			return indicador;
		});
		setSubtitle(indicador?.indicator.iqc001Descricao);
		setValue("indicator", indicador?.indicator);
		setValue("metrics",indicador?.metrics);
		setValue("fields",indicador?.fields);
		setValue("compromise",indicador?.compromise && indicador?.compromise !== null ? indicador?.compromise : baseIqtbc002);
		setValue("competitiveness",indicador?.competitiveness && indicador?.competitiveness !== null ? indicador?.competitiveness : baseIqtbc002);
		setValue("goal",indicador?.goal);
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange
	}));

	const setLocalFields= (fields:IIqtbc002[] = []) => {
		const  temporaryFields = [] as Array<{value:string,label:string}>;
		fields.forEach((field) => 
		{
			if(field.iqc002Iqb001Id){
				temporaryFields.push({value:field.iqc002Iqb001Id.toString(),label:field.description||""});
			} 
		});
		return temporaryFields;
	};

	return (
		<Modal
			ref={modalIndicator}
			header={{
				title:modalTitle,
				icon: {
					name: "chartBar"
				},
				subtitle: subtitle ? `${translate("Indicador")}: ${subtitle}` : undefined
			}}
			footer={{
				showSaveButton: true,
				buttonLoadingText: translate("Carregando"),
				saveButtonText: translate("Salvar"),
				cancelButtonText: translate("Cancelar"),
				childrenOfStandardFooter: <>
					{isAdd &&
						<RCCButton type="button" color="blue" 
							size="sm"
							onClick={() => { setSimpleForm((prevState) => !prevState); }}
						>
							{translate(`Ver cadastro ${simpleForm ? "completo" :"simplificado"}`)}
						</RCCButton>
					}
				</>
			}}
			withoutForm
			onSubmit={handleSubmit(submitIndicator,() => addToast({
				color: "yellow",
				description: translate("Verifique os campos e tente novamente"),
				title: translate("Atenção")
			}))}
		>
				
			<Tabs
				name="indicatorsTab"
				tabInit={0}
				options={
					[
						{
							label: translate("Dados"),
							content: 
								simpleForm ? <TabSimpleForm
									indicator={indicator?.indicator}
									goal={indicator.goal} 
									tags={indicator.tags}
									errors={formState.errors}
									register={register}
									control={control}
									setValue={setValue}
									isDuplicate={isDuplicate}
								/>
									: <TabGeneralData 
										indicator={indicator?.indicator}
										tags={indicator.tags}
										errors={formState.errors}
										register={register}
										control={control}
										setValue={setValue}
										isDuplicate={isDuplicate}
									/>
						},
						{
							label: translate("Metas"),
							content: 
								<TabGoals 
									isAdd={isAdd||isDuplicate} 
									competitiveness={indicator.competitiveness} 
									goal={indicator.goal} 
									compromise={indicator.compromise}
									orderGoals={indicator.indicator.iqc001Ordemmetas}
									fields={setLocalFields(fieldsControls.fields)}
									errors={formState.errors}
									register={register}
									control={control}
									setValue={setValue}
									hidden={simpleForm}
									selectedIndicator={indicator.indicator.iqc001Id}
								/>,
							hidden: simpleForm
						},
						{
							label: translate("Campos"),
							content: 
								<TabFields 
									indicatorsFields ={fieldsControls.fields}
									simpleField={simpleForm}
									selectFieldtoGoal={indicator.selectedGoalField}
									errors={formState.errors}
									register={register}
									control={control}
									clearErrors={clearErrors}
									setValue={setValue}
									selectedIndicator={indicator.indicator.iqc001Id}
								/>
						},
						{
							label: translate("Métricas"),
							content: 
								<TabMetrics  
									defaultMetricId={indicator.indicator.iqc001Taa006Id} 
									metrics={metricControls.fields} 
									useArrayControls={metricControls} 
									errors={formState.errors}
									register={register}
									control={control}
									clearErrors={clearErrors}
									setValue={setValue}
									hidden={simpleForm}
								/>,
							hidden: simpleForm
						},
						{
							label: translate("Permissões"),
							content: 
								<TabPermissions  
									permissions={indicator.permissions} 
									errors={formState.errors}
									register={register}
									control={control}
									clearErrors={clearErrors}
									unregister={unregister}
								/>
						},
						{
							label: translate("Gráficos"),
							content: 
								<TabGraphic
									indicator={indicator?.indicator} 
									errors={formState.errors}
									register={register}
									control={control}
									hidden={simpleForm}
								/>,
							hidden: simpleForm
						},
						{
							label: translate("Avançado"),
							content: 
								<TabAdvanced 
									indicator={indicator?.indicator} 
									errors={formState.errors}
									register={register}
									control={control}
									hidden={simpleForm}
								/>,
							hidden: simpleForm
						}
					]
				}
			/>
		</Modal>
	);
};

export default forwardRef(IndicatorModal);

